import { defineStore } from 'pinia';
import { exportApiService } from 'src/services';
import type { dynamicObj } from 'src/pages/assets';
import encryptionUtil from 'src/utils/encryptionUtil';

export const useDocExportStore = defineStore('doc_export', {
  state: () => ({
    configurations: [],
    processing: false,
    config_saved: false,
    selected_export: '',
    selected_configuration: {} as dynamicObj,
    export_types: {
      PULL: 'pull',
      WEBHOOK: 'webhook',
      FLOW2: 'flow2',
      WATCHER: 'watcher',
      INFOR_ION: 'infor-ion',
      INFOR_IDM: 'infor-idm',
      SFTP: 'sftp',
      INFORSFTP: 'infor_sftp',
      SMB_WITH_JPL: 'smb_with_jpl',
      INFOR_CLOUD: 'infor-cloud',
      GENERIC_API: 'generic_api',
      INFOR_ONPREMISE: 'infor-onpremise',
      INFOR_IDM_CLOUD: 'infor-idm-cloud',
      INFOR_BOD_CLOUD: 'infor-bod-cloud',
      INFOR_ION_CLOUD: 'infor-ion-cloud',
      INFOR_LN_CLOUD: 'infor-ln-cloud',
      INFOR_M3_CLOUD: 'infor-m3-cloud',
      INFOR_M3_TOML_CLOUD: 'infor-m3-toml-cloud',
      INFOR_M3_OC_CHARGES_ONPREMISE: 'infor-m3-oc-charges-onpremise',
      INFOR_IDM_ON_PREMISE: 'infor-idm-onpremise',
      INFOR_BOD_ON_PREMISE: 'infor-bod-onpremise',
      INFOR_ION_ON_PREMISE: 'infor-ion-onpremise',
      INFOR_GLS840MI: 'infor-gls840-onpremise',
      INFOR_M3_PACKING_UNIT_ONPREMISE: 'infor-m3-packing-unit-onpremise',
      VERTEX: 'vertex',
    },
  }),
  getters: {
    getData(state) {
      return state;
    },
  },
  actions: {
    async getTomlFile() {
      const response: dynamicObj = await exportApiService.getTomlFile();
      return response;
    },
    setSelectedExportForEdit(configuration: any) {
      this.selected_configuration = configuration;

      // Normalize Generic API: API returns 'generic-api-onpremise' for on_premise=true,
      // 'generic_api' for on_premise=false. Map both to the single GENERIC_API constant.
      if (configuration.export_method === 'generic-api-onpremise') {
        configuration.export_method = this.export_types.GENERIC_API;
        configuration._generic_api_on_premise = true;
      }

      // Handle Watchdog configs differently - they use string UUIDs and 'name' field
      if (configuration.export_method === this.export_types.WATCHER) {
        this.selected_configuration.id = configuration.id; // Keep as string
        // Ensure 'name' is set (may have been mapped to 'title' in getExportConfigurations)
        if (!this.selected_configuration.name && configuration.title) {
          this.selected_configuration.name = configuration.title;
        }
        // Map is_active to enabled for UI binding
        this.selected_configuration.enabled = configuration.is_active ?? true;
        // Extract watcher-specific fields from additional_attributes
        const attrs = configuration.additional_attributes || {};
        this.selected_configuration.xslt_template_id = attrs.xslt_template_id || null;
        this.selected_configuration.export_pdf_enabled = attrs.export_pdf_enabled || false;
        this.selected_configuration.pdf_export_folder = attrs.pdf_export_folder || '';
      } else {
        this.selected_configuration.id = Number(configuration.id);
      }

      this.selected_configuration.zugferd = Boolean(configuration.zugferd);

      // Map API field names to form field names for SFTP and SMB
      if (configuration.export_method === this.export_types.SFTP ||
          configuration.export_method === this.export_types.SMB_WITH_JPL) {
        // API returns server_url and port, form uses export_url and export_port
        if (configuration.server_url) {
          this.selected_configuration.export_url = configuration.server_url;
        }
        if (configuration.port) {
          this.selected_configuration.export_port = configuration.port;
        }
        if (configuration.password) {
          const ctx = (encryptionUtil as any).data();
          this.selected_configuration.password = (encryptionUtil as any).methods.decryptPassword.call(ctx, configuration.password);
        }
      }

      // Parse additional_attributes for SFTP, SMB, and InforSFTP advanced settings
      if (configuration.export_method === this.export_types.SFTP ||
          configuration.export_method === this.export_types.SMB_WITH_JPL ||
          configuration.export_method === this.export_types.INFORSFTP) {
        if (configuration.additional_attributes) {
          try {
            const attrs = typeof configuration.additional_attributes === 'string'
              ? JSON.parse(configuration.additional_attributes)
              : configuration.additional_attributes;
            this.selected_configuration.filename_export_mode = attrs.export_with_docbits_id_as_filename || false;
            this.selected_configuration.export_document_history = attrs.include_document_history || false;
          } catch (e) {
            // Ignore parse errors
          }
        }
      }

      // Map API field names to form field names for GLS840MI, OC Charges, and Packing Unit
      if (configuration.export_method === this.export_types.INFOR_GLS840MI ||
          configuration.export_method === this.export_types.INFOR_M3_OC_CHARGES_ONPREMISE ||
          configuration.export_method === this.export_types.INFOR_M3_PACKING_UNIT_ONPREMISE) {
        if (configuration.api_export_mapping_id) {
          this.selected_configuration.m3_toml_mapping_id = configuration.api_export_mapping_id;
        }
      }

      // Map API field names to form field names for Vertex
      if (configuration.export_method === this.export_types.VERTEX) {
        if (configuration.vertex_mapping_id) {
          this.selected_configuration.m3_toml_mapping_id = configuration.vertex_mapping_id;
        }
      }

      // Map API field names to form field names for Generic API
      if (configuration.export_method === this.export_types.GENERIC_API) {
        if (configuration.api_export_mapping_id) {
          this.selected_configuration.m3_toml_mapping_id = configuration.api_export_mapping_id;
        }
        if (configuration.api_credentials_mapping_id) {
          this.selected_configuration.api_credentials_mapping_id = configuration.api_credentials_mapping_id;
        }
      }

      switch (configuration.export_method) {
        case this.export_types.INFOR_ION_ON_PREMISE:
        case this.export_types.INFOR_IDM_ON_PREMISE:
        case this.export_types.INFOR_BOD_ON_PREMISE:
          this.selected_configuration.cloud = false;
          break;
        case this.export_types.INFOR_ION_CLOUD:
        case this.export_types.INFOR_IDM_CLOUD:
        case this.export_types.INFOR_BOD_CLOUD:
          this.selected_configuration.cloud = true;
          break;
        case this.export_types.GENERIC_API:
          this.selected_configuration.cloud = !configuration._generic_api_on_premise;
          break;
      }

      this.selected_export = configuration.export_method;
    },

    selectedNewExport(val: string) {
      this.selected_export = val;
      this.setModelForSelectedConfig();
      this.config_saved = false;
    },

    // Configuration schemas for each export type - centralized field definitions
    getModelSchema(exportType: string): dynamicObj {
      // Reusable field groups
      const base = { doc_type: '', title: '', id: 0 };
      const credentials = { username: '', password: '' };
      const server = { export_url: '', export_port: '', export_folder: '' };
      const ionApi = { ion_file: null, infor_ion_api: null };
      const idmMapping = { idm_file: null, infor_idm_mapping: null };
      const bodMapping = { bod_file: null, infor_bod_mapping: null };
      const m3Auto = { auto_approve_after_export: false, auto_validate_after_export: false };
      const cloud = { cloud: true };

      // Schema definitions by export type
      const schemas: Record<string, dynamicObj> = {
        [this.export_types.PULL]: { ...base },
        [this.export_types.WEBHOOK]: { ...base, export_url: '' },
        [this.export_types.SFTP]: { ...base, ...credentials, ...server, export_xslt: null, export_xslt_file: null },
        [this.export_types.SMB_WITH_JPL]: { ...base, ...credentials, ...server, jpl_mapping: null, jpl_mapping_file: null },
        [this.export_types.INFORSFTP]: { ...base, export_folder: '', infor_idm_mapping: null, infor_bod_mapping: null },
        [this.export_types.INFOR_IDM_CLOUD]: { ...base, ...ionApi, ...idmMapping, ...cloud, export_method: '' },
        [this.export_types.INFOR_IDM_ON_PREMISE]: { ...base, ...ionApi, ...idmMapping, ...cloud, export_method: '' },
        [this.export_types.INFOR_BOD_CLOUD]: { ...base, ...ionApi, ...bodMapping, ...cloud },
        [this.export_types.INFOR_BOD_ON_PREMISE]: { ...base, ...ionApi, ...bodMapping, ...cloud },
        [this.export_types.INFOR_ION_CLOUD]: { ...base, ...ionApi, ...idmMapping, ...bodMapping, ...cloud },
        [this.export_types.INFOR_ION_ON_PREMISE]: { ...base, ...ionApi, ...idmMapping, ...bodMapping, ...cloud },
        [this.export_types.INFOR_LN_CLOUD]: { ...base, ...ionApi, ...idmMapping, ln_file: null, infor_ln_mapping: null },
        [this.export_types.INFOR_M3_CLOUD]: { ...base, ...ionApi, ...idmMapping, m3_file: null, infor_m3_mapping: null, ...m3Auto },
        [this.export_types.INFOR_M3_TOML_CLOUD]: { ...base, ...ionApi, ...idmMapping, m3_file: null, toml_file: null, infor_m3_toml_mapping: null, ...m3Auto },
        [this.export_types.INFOR_M3_OC_CHARGES_ONPREMISE]: { ...base, ...ionApi, infor_ion_api_id: null, toml_file: null, infor_m3_toml_mapping: null, m3_toml_mapping_id: null, api_export_mapping_id: null, ...m3Auto },
        [this.export_types.INFOR_GLS840MI]: { ...base, ...ionApi, infor_ion_api_id: null, toml_file: null, infor_m3_toml_mapping: null, m3_toml_mapping_id: null, api_export_mapping_id: null },
        [this.export_types.INFOR_M3_PACKING_UNIT_ONPREMISE]: { ...base, ...ionApi, infor_ion_api_id: null, toml_file: null, infor_m3_toml_mapping: null, m3_toml_mapping_id: null, api_export_mapping_id: null },
        [this.export_types.VERTEX]: { ...base, toml_file: null, infor_m3_toml_mapping: null, m3_toml_mapping_id: null, vertex_mapping_id: null },
        [this.export_types.GENERIC_API]: { ...base, ...cloud, toml_file: null, infor_m3_toml_mapping: null, m3_toml_mapping_id: null, api_export_mapping_id: null, credentials_file: null, api_credentials_mapping: null, api_credentials_mapping_id: null, export_method: '' },
        [this.export_types.FLOW2]: { ...base, ...credentials, flow2_mapping_file: null, flow2_mapping: null, doc_type_id: '', flow2_url: '', cabinet_id: '' },
        [this.export_types.WATCHER]: { name: '', config_type: 'export', enabled: true, export_folder: '', xslt_template_id: null, doc_type: '', sub_org_id: null, id: '', export_pdf_enabled: false, pdf_export_folder: '' },
      };

      return schemas[exportType] || { ...base };
    },

    setModelForSelectedConfig() {
      // Preserve common fields that should persist across type changes
      // Watcher uses 'name' instead of 'title', so grab both
      const currentTitle = this.selected_configuration.title || this.selected_configuration.name || '';
      const preservedFields = {
        doc_type: this.selected_configuration.doc_type || '',
        sub_org_id: this.selected_configuration.sub_org_id || null,
      };

      // Set configuration from schema
      this.selected_configuration = { ...this.getModelSchema(this.selected_export) };

      // Restore preserved common fields after model reset
      // Watcher uses 'name', all other types use 'title'
      if (this.selected_export === this.export_types.WATCHER) {
        this.selected_configuration.name = currentTitle;
      } else {
        this.selected_configuration.title = currentTitle;
      }
      this.selected_configuration.doc_type = preservedFields.doc_type;
      this.selected_configuration.sub_org_id = preservedFields.sub_org_id;
    },

    async getExportConfigurations() {
      try {
        this.processing = true;

        // Fetch regular export configurations
        const response = await exportApiService.getAll();
        let regularConfigs: any[] = [];
        if (
          response &&
          (!response.hasOwnProperty('success') || response.success !== false)
        ) {
          regularConfigs = response.data || [];
        }

        // Fetch Watchdog configurations and filter for export type only
        let watchdogConfigs: any[] = [];
        try {
          const watchdogResponse =
            await exportApiService.getWatchdogConfigurations();
          if (
            watchdogResponse &&
            watchdogResponse.configurations &&
            Array.isArray(watchdogResponse.configurations)
          ) {
            // Filter to only include export configs and normalize structure
            watchdogConfigs = watchdogResponse.configurations
              .filter((config: any) => config.config_type === 'export')
              .map((config: any) => ({
                ...config,
                title: config.name, // Map name to title for consistency
                export_method: 'watcher',
                is_active: config.enabled,
                is_watchdog: true, // Flag to identify Watchdog configs
              }));
          }
        } catch (watchdogErr: any) {
          // Silently handle Watchdog API errors - don't break regular configs
          console.warn('Failed to fetch Watchdog configurations:', watchdogErr);
        }

        // Merge and sort configurations by: Document Type → Sub-Org → Execution Order
        const allConfigs = [...regularConfigs, ...watchdogConfigs];
        this.configurations = allConfigs.sort((a: any, b: any) => {
          // First: Sort by doc_type
          const docTypeA = (a.doc_type || '').toLowerCase();
          const docTypeB = (b.doc_type || '').toLowerCase();
          if (docTypeA !== docTypeB) {
            return docTypeA.localeCompare(docTypeB);
          }

          // Second: Sort by sub_org_id
          const subOrgA = (a.sub_org_id || '').toLowerCase();
          const subOrgB = (b.sub_org_id || '').toLowerCase();
          if (subOrgA !== subOrgB) {
            return subOrgA.localeCompare(subOrgB);
          }

          // Third: Sort by execution_order
          const orderA = a.execution_order || 0;
          const orderB = b.execution_order || 0;
          return orderA - orderB;
        });
      } catch (err: any) {
        console.error('Failed to fetch export configurations:', err);
      } finally {
        this.processing = false;
        // NOTE: Do NOT call clearStateForExportDialog() here!
        // It was causing selected_configuration to be reset when editing,
        // because loadRequiredData() calls getExportConfigurations() on mount.
        // State should only be cleared explicitly when navigating away.
      }
    },

    clearStateForExportDialog() {
      this.clearOnlySelectionConfigState();
    },

    async deleteConfiguration(id: number, isWatchdog: boolean = false) {
      try {
        this.processing = true;
        if (isWatchdog) {
          await exportApiService.deleteWatchdogConfiguration(id.toString());
        } else {
          await exportApiService.deleteRecord(id.toString());
        }
        await this.getExportConfigurations();
      } catch (err: any) {
        console.error('Failed to delete configuration:', err);
      } finally {
        this.processing = false;
      }
    },

    clearOnlySelectionConfigState() {
      this.selected_configuration = {};
      this.config_saved = false;
      this.processing = false;
      this.selected_export = '';
    },

    clearConfiguration() {
      const id = Number(this.selected_configuration.id);
      if (id === 0) {
        this.clearOnlySelectionConfigState();
      }

      if (id !== 0) {
        // logInfo( 'clearConfiguration 2');
        this.clearStateForExportDialog();
      }
    },

  },
});
